
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { RequiredValidator } from "@dev/hc-components";
import FactorEnum from "@/app/dto/FactorEnum";
import PhoneNumber from "@/app/dto/PhoneNumber";
import ProcessEnum from "@/app/dto/ProcessEnum";
import ProcessResultKey from "@/app/dto/ProcessResultKeyEnum";
import Property from "@/app/dto/Property";
import IApiError from "@/app/model/IApiError";
import KeyResult from "@/app/model/KeyResult";
import OtpData from "@/app/model/OtpData";
import OtpResult from "@/app/model/OtpResult";
import RouteName from "@/app/router/NameEnum";
import AuthorizationService from "@/app/service/authorization";
import ContractWithdrawalService from "@/app/service/contract";
import Countdown from "@/components/countdown";

@Component({
    components: {
        Countdown
    }
})
export default class ContractWithdrawalOtp extends Vue {
    @Prop({ type: String }) token!: string;
    @Prop({ type: String }) contractNumber!: string;
    @Prop({ type: String }) email!: string;
    @Prop({ type: String }) countryCode!: string;
    @Prop({ type: String }) number!: string;

    private keyResult: KeyResult | null = null;
    private phoneNumber: string = "";
    private waiting: boolean = false;
    private otpValue: string = "";
    private otpValidator: RequiredValidator = new RequiredValidator(this.$localize("sms.required"));
    private smsInvalid: boolean = false;
    private otpErrors = [ProcessResultKey.OTP_LIMIT_REACHED, ProcessResultKey.OTP_NOT_READY_YET, ProcessResultKey.OTP_PHONE_BLOCKED];
    private submitting: boolean = false;

    private get getWaitUntilBlockId(): string {
        return this.isBlockedWaitUntil ? "withBlockUntil" : "withWaitUntil";
    }

    private get isBlockedWaitUntil(): boolean {
        return this.isNotReadyYet || this.isLimitReached || this.isPhoneBlocked || this.isAuthorizationBlocked;
    }

    private get getBlockedWaitUntilReason(): string {
        let reasonTranslationKey: string = "";
        if (this.isNotReadyYet) {
            reasonTranslationKey = "sms.not_ready_yet";
        } else if (this.isLimitReached) {
            reasonTranslationKey = "sms.too_many_attempts";
        } else if (this.isPhoneBlocked) {
            reasonTranslationKey = "sms.phone_blocked";
        } else if (this.isAuthorizationBlocked) {
            reasonTranslationKey = "sms.authorization_blocked";
        }

        return this.$localize(reasonTranslationKey);
    }

    private get isNotReadyYet(): boolean {
        return !!this.keyResult?.isNotReadyYet();
    }

    private get isLimitReached(): boolean {
        return !!this.keyResult?.isLimitReached();
    }

    private get isPhoneBlocked(): boolean {
        return !!this.keyResult?.isPhoneBlocked();
    }

    private get isAuthorizationBlocked(): boolean {
        return !!this.keyResult?.isAuthorizationBlocked();
    }

    private get isValid(): boolean {
        return this.otpValidator.validate(this.otpValue);
    }

    private onCountdownEnd(): void {
        this.waiting = false;
    }

    private onSubmit(): void {
        this.clearErrors();
        if (this.isValid) {
            this.submitting = true;
            ContractWithdrawalService.terminate(
                this.token,
                this.keyResult?.getAuthorizationKey() || "",
                new OtpData(this.otpValue, this.keyResult?.getOtpKey() || ""),
                this.contractNumber
            )
                .then(() => {
                    this.$router.push({
                        name: RouteName.CONTRACT_WITHDRAWAL_RESULT,
                        params: {
                            email: this.email
                        }
                    });
                })
                .catch((error: IApiError) => {
                    this.processTerminateError(error);
                })
                .finally(() => {
                    this.submitting = false;
                });
        }
    }

    private createKeyRequest(sendOtp: boolean): void {
        const mobile = new PhoneNumber(this.countryCode, this.number);
        const data = [new Property("ContractNumber", this.contractNumber)];
        AuthorizationService.authorizationKeyProtected(this.token, [FactorEnum.OTP], sendOtp, ProcessEnum.CONTRACT_WITHDRAWAL, mobile, data)
            .then((response: KeyResult) => {
                if (this.keyResult) {
                    this.keyResult?.mergeKey(response);
                } else {
                    this.keyResult = response;
                }
                this.processKeyResult();
            })
            .catch((error: IApiError) => {
                this.routeToError();
            });
    }

    private sendOtpRequest(): void {
        if (this.keyResult?.authKey) {
            const mobile = new PhoneNumber(this.countryCode, this.number);
            const data = [new Property("ContractNumber", this.contractNumber)];
            AuthorizationService.sendOtpProtected(this.token, this.keyResult?.getAuthorizationKey() || "", ProcessEnum.CONTRACT_WITHDRAWAL, mobile, data)
                .then((response: OtpResult) => {
                    this.keyResult?.mergeOtp(response);
                    this.processKeyResult();
                })
                .catch((error: IApiError) => {
                    this.routeToError();
                });
        } else {
            this.createKeyRequest(true);
        }
    }

    private processKeyResult(): void {
        if (!!this.keyResult?.authKey || this.otpErrors.includes(this.keyResult?.error?.status as ProcessResultKey)) {
            this.setupPhone();
            this.setupInitWaiting();
        } else {
            !!this.keyResult?.error ? this.processKeyError(this.keyResult?.error) : this.routeToError();
        }
    }

    private setupPhone(): void {
        if (!!this.keyResult?.getPhoneNumber()) {
            this.phoneNumber = this.keyResult?.getPhoneNumber() || "";
        }
    }

    private setupInitWaiting(): void {
        let waitUntil: Date | undefined = this.keyResult?.getWaitUntil();
        this.waiting = !!waitUntil && waitUntil > new Date();
    }

    private processKeyError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";

        switch (error.status) {
            case ProcessResultKey.AUTHORIZATION_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.authorization.blocked.message");
                break;
        }

        this.routeToError({ heading: errorHeading, message: errorMessage });
    }

    private processTerminateError(error: IApiError): void {
        if (error.status == ProcessResultKey.AUTHORIZATION_INVALID) {
            this.createKeyRequest(false);
            this.smsInvalid = true;
        } else {
            let errorHeading: string = "";
            let errorMessage: string = "";

            switch (error.status) {
                case ProcessResultKey.CONTRACT_NOT_FOUND:
                    errorHeading = this.$localize("error.contract.not_found.title");
                    errorMessage = this.$localize("error.contract.not_found.message");
                    break;
                case ProcessResultKey.INVALID_CONTRACT:
                    errorHeading = this.$localize("error.contract.invalid_state.title");
                    errorMessage = this.$localize("error.contract.invalid_state.message");
                    break;
            }
            this.routeToError({ heading: errorHeading, message: errorMessage });
        }
    }

    private clearErrors(): void {
        this.smsInvalid = false;
    }

    private routeToPhone(): void {
        this.$router.push({ name: RouteName.CONTRACT_WITHDRAWAL_PHONE });
    }

    private routeToEmail(): void {
        this.$router.push({ name: RouteName.CONTRACT_WITHDRAWAL_EMAIL });
    }

    private routeToError(params?: { heading: string; message: string }) {
        this.$router.push({ name: RouteName.CONTRACT_WITHDRAWAL_ERROR, params });
    }

    created() {
        this.createKeyRequest(true);
    }
}
